/*
Copyright (C) 2017  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import React from "react";
import styled from "styled-components";
import { observer } from "mobx-react";
import { onSwiftRefresh } from "@src/components/modules/TemplateModule/TopBar/refresh";
import { useNavigate } from "react-router";

import MainTemplate from "@src/components/modules/TemplateModule/MainTemplate";
import Navigation from "@src/components/modules/NavigationModule/Navigation";
import FilterList from "@src/components/ui/Lists/FilterList";
import PageHeader from "@src/components/smart/PageHeader";
import ProjectListItem from "@src/components/modules/ProjectModule/ProjectListItem";
import { PROJECT_COLUMNS } from "@src/components/modules/ProjectModule/ProjectListItem/ProjectListItem";
import {
  ClearFiltersButton,
  Segmented,
  ToolbarSearch,
  matchesSearch,
  useListQuery,
} from "@src/components/ui/Lists/ListToolbar";
import type { ListQuery } from "@src/components/ui/Lists/ListToolbar";
import type { DropdownAction } from "@src/components/ui/Dropdowns/ActionDropdown";
import { FolderKanban } from "lucide-react";

import type { Project, RoleAssignment } from "@src/@types/Project";

import projectStore from "@src/stores/ProjectStore";
import userStore from "@src/stores/UserStore";
import configLoader from "@src/utils/Config";

const Wrapper = styled.div<any>``;

type State = {
  modalIsOpen: boolean;
};

type Props = {
  onNavigate: (path: string) => void;
  query: ListQuery;
};

@observer
class ProjectsPage extends React.Component<Props, State> {
  state = {
    modalIsOpen: false,
  };

  pollTimeout = 0;

  stopPolling = false;

  offSwiftRefresh?: () => void;

  componentDidMount() {
    // The top bar's Refresh reloads this page's data.
    this.offSwiftRefresh = onSwiftRefresh(() => {
      this.handleReloadButtonClick();
    });
    document.title = "Projects · SwiftIQ";

    this.stopPolling = false;
    this.pollData(true);
  }

  componentWillUnmount() {
    this.offSwiftRefresh?.();
    clearTimeout(this.pollTimeout);
    this.stopPolling = true;
  }

  getMembers(projectId: string): number {
    return projectStore.roleAssignments
      .filter(a => a.scope.project?.id === projectId)
      .reduce((uniqueRoles, role) => {
        if (!uniqueRoles.find(p => p.user.id === role.user.id)) {
          uniqueRoles.push(role);
        }
        return uniqueRoles;
      }, [] as RoleAssignment[]).length;
  }

  isCurrentProject(projectId: string): boolean {
    const project =
      userStore.loggedUser && userStore.loggedUser.project
        ? userStore.loggedUser.project
        : null;
    return project ? project.id === projectId : false;
  }

  handleModalOpen() {
    this.setState({ modalIsOpen: true });
  }

  handleModalClose() {
    this.setState({ modalIsOpen: false }, () => {
      this.pollData();
    });
  }

  handleReloadButtonClick() {
    projectStore.getProjects({ showLoading: true });
    projectStore.getRoleAssignments();
  }

  async handleSwitchProjectClick(projectId: string) {
    await userStore.switchProject(projectId);
    projectStore.getProjects();
  }

  async pollData(showLoading?: boolean) {
    if (this.state.modalIsOpen || this.stopPolling) {
      return;
    }

    await Promise.all([
      projectStore.getProjects({ showLoading, skipLog: true }),
      projectStore.getRoleAssignments({ skipLog: true }),
    ]);
    this.pollTimeout = window.setTimeout(() => {
      this.pollData();
    }, configLoader.config.requestPollTimeout);
  }

  get filtersActive() {
    return Boolean(this.props.query.get("q") || this.props.query.get("enabled"));
  }

  clearFilters() {
    this.props.query.set({ q: null, enabled: null });
  }

  filteredProjects(except?: string): Project[] {
    const q = this.props.query;
    const text = q.get("q");
    const enabled = q.get("enabled");
    return projectStore.projects
      .filter(p => {
        if (except !== "enabled" && enabled === "yes" && p.enabled === false) return false;
        if (except !== "enabled" && enabled === "no" && p.enabled !== false) return false;
        if (text) return matchesSearch(text, [p.name, p.description, p.id]);
        return true;
      })
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  renderToolbar() {
    const q = this.props.query;
    const forEnabled = this.filteredProjects("enabled");
    return (
      <>
        <ToolbarSearch value={q.get("q")} placeholder="Search projects" onChange={v => q.set({ q: v })} />
        <Segmented
          label="Status"
          value={q.get("enabled")}
          onChange={v => q.set({ enabled: v })}
          options={[
            { value: "", label: "All" },
            { value: "yes", label: "Enabled", count: forEnabled.filter(p => p.enabled !== false).length },
            { value: "no", label: "Disabled", count: forEnabled.filter(p => p.enabled === false).length },
          ]}
        />
        {this.filtersActive ? <ClearFiltersButton onClick={() => this.clearFilters()} /> : null}
      </>
    );
  }

  rowActions(project: Project): DropdownAction[] {
    return [
      { label: "Open", action: () => this.props.onNavigate(`/projects/${project.id}`) },
      {
        label: "Switch to project",
        disabled: this.isCurrentProject(project.id) || project.enabled === false,
        action: () => {
          this.handleSwitchProjectClick(project.id);
        },
      },
    ];
  }

  render() {
    return (
      <Wrapper>
        <MainTemplate
          navigationComponent={<Navigation currentPage="projects" />}
          listNoMargin
          listComponent={
            <FilterList
              toolbar={this.renderToolbar()}
              filtersActive={this.filtersActive}
              onClearFilters={() => this.clearFilters()}
              columns={PROJECT_COLUMNS}
              selectionLabel="project"
              loading={projectStore.loading}
              items={this.filteredProjects()}
              onItemClick={(user: Project) => {
                this.props.onNavigate(`/projects/${user.id}`);
              }}
              onReloadButtonClick={() => {
                this.handleReloadButtonClick();
              }}
              renderItemComponent={component => (
                <ProjectListItem
                  {...component}
                  getMembers={projectId => this.getMembers(projectId)}
                  isCurrentProject={projectId =>
                    this.isCurrentProject(projectId)
                  }
                  onSwitchProjectClick={projectId =>
                    this.handleSwitchProjectClick(projectId)
                  }
                  actions={this.rowActions(component.item)}
                />
              )}
              emptyListIcon={FolderKanban}
              emptyListMessage="No projects yet"
              emptyListExtraMessage="Projects keep endpoints, transfers and their members together. Add one from the New menu."
              itemsPerPageOptions={[10, 25, 50]}
              initialItemsPerPage={10}
            />
          }
          headerComponent={
            <PageHeader
              title="Projects"
              subtitle="Workspaces that keep endpoints, transfers and their members together."
              onModalOpen={() => {
                this.handleModalOpen();
              }}
              onModalClose={() => {
                this.handleModalClose();
              }}
            />
          }
        />
      </Wrapper>
    );
  }
}

function ProjectsPageWithNavigate() {
  const navigate = useNavigate();
  const query = useListQuery();

  return <ProjectsPage onNavigate={navigate} query={query} />;
}

export default ProjectsPageWithNavigate;
